import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides, seeCoachSteps, snatchPhases } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-white" data-id="element-200" data-mp="3dqyzcn6vxzwc">
      <div className="max-w-5xl mx-auto" data-id="element-201" data-mp="3dpnxisx0327v">
        <div className="text-center mb-10" data-id="element-202" data-mp="38qukt2pjff8r">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-blue-950" data-id="element-203" data-mp="38rvvv76k01qh">
            See Inside Olympic Weightlifting Coach Ready
          </h2>
          <p className="text-lg text-slate-700 max-w-3xl mx-auto" data-id="element-204" data-mp="2h0q0t55owu82">
            Real pages from the system — visual technique breakdowns, bar-path diagrams,
            step-by-step lift sequences, correct-vs-incorrect comparisons, ready-to-use drills,
            visual progressions, coaching checklists and professional athlete-development tools.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-6 md:gap-10 items-start mb-12" data-id="element-205" data-mp="1xc784dlh6yom">
          <div data-id="element-206" data-mp="1xacvrno9sew5">
            <p className="text-xl font-serif font-bold text-blue-950 mb-3" data-id="element-207" data-mp="19c7uov5cvlv">
              This is <span className="text-amber-600" data-id="element-208" data-mp="685q0uldpasd">not</span> a text-heavy Weightlifting
              manual.
            </p>
            <p className="text-slate-700 leading-relaxed" data-id="element-209" data-mp="ayccansf4j7g">
              The material was designed to help coaches SEE the movement, SEE the technical
              problem, SEE the correction and then coach it.
            </p>
          </div>
          <div className="bg-slate-50 border border-amber-500/40 rounded-2xl p-6" data-id="element-210" data-mp="3eh5oaw5jszzi">
            <p className="text-sm font-bold text-blue-700 uppercase tracking-wider mb-4" data-id="element-211" data-mp="3jfgumwwmwscz">
              The 6 phases of the Snatch
            </p>
            <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5" data-id="element-212" data-mp="3sfdp5f5pglfy">
              {snatchPhases.map((phase, phaseIndex) => <li key={phase} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-213" data-mp="13o2up913279d" data-mpi={phaseIndex}>
                  <span className="w-5 h-5 rounded-full bg-blue-950 text-amber-500 text-[11px] font-bold flex items-center justify-center shrink-0" data-id="element-214" data-mp="17ollczr8p79z">
                    {phaseIndex + 1}
                  </span>
                  <span className="font-semibold uppercase" data-id="element-215" data-mp="2o5bzan6cssol">{phase}</span>
                </li>)}
            </ol>
          </div>
        </div>

        <p className="text-center text-blue-950 font-bold mb-4" data-id="element-216" data-mp="2jciszuv8lcae">
          Swipe through real pages from Olympic Weightlifting Coach Ready
        </p>

        <div className="relative" data-id="element-217" data-mp="29hqumottylek">
          <div className="relative bg-slate-50 border border-amber-500/40 rounded-2xl shadow-sm shadow-blue-700/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-218" data-mp="29el5xap45ljd">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-219" data-mp="1vi7yyvi7h2j5">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}: ${slide.title}`} data-id="element-220" data-mp="3uuag3z3cxktn">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-221" data-mp="1yto1vh19x8tn" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-blue-950/90 text-amber-500 flex items-center justify-center hover:bg-blue-950 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-label="View image larger" data-id="element-222" data-mp="1676674cq7n7w">
              <Maximize2 size={18} data-id="element-223" data-mp="1p58zhvcb46bi" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-blue-950 text-amber-500 border border-amber-500/50 shadow-lg flex items-center justify-center hover:bg-blue-700 hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-label="Previous image" data-id="element-224" data-mp="1k8ij16wlb8pk">
            <ChevronLeft size={24} data-id="element-225" data-mp="2azg19k5r40us" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-blue-950 text-amber-500 border border-amber-500/50 shadow-lg flex items-center justify-center hover:bg-blue-700 hover:text-white transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-label="Next image" data-id="element-226" data-mp="2b0r2bc7taui7">
            <ChevronRight size={24} data-id="element-227" data-mp="2ymxl8tirxun7" />
          </button>
        </div>

        <p className="text-center text-blue-950 font-bold text-sm sm:text-base mt-6" data-id="element-228" data-mp="33nbuug00xcnc">{slide.title}</p>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-229" data-mp="38myaew3br4zb">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 ${dotIndex === index ? 'w-8 bg-amber-500' : 'w-2.5 bg-blue-950/25 hover:bg-blue-950/40'}`} aria-label={`Go to image ${dotIndex + 1}: ${item.title}`} aria-current={dotIndex === index} data-id="element-230" data-mp="19b57kituv5eo" data-mpi={dotIndex} />)}
        </div>

        <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-10" data-id="element-231" data-mp="ebdjhqnlrmt0">
          {seeCoachSteps.map((step, stepIndex) => <li key={step} className={`flex items-center gap-1.5 text-lg font-serif font-bold uppercase ${stepIndex === seeCoachSteps.length - 1 ? 'text-amber-600' : 'text-blue-950'}`} data-id="element-232" data-mp="a8p8pdfbw5te" data-mpi={stepIndex}>
              <CheckCircle2 size={16} className="text-amber-500 shrink-0" data-id="element-233" data-mp="2q2zah0pulzqq" />
              {step}
            </li>)}
        </ul>
      </div>

      <AnimatePresence data-id="element-234" data-mp="2l57jqa0xwjo4">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-blue-950/95 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.title} onClick={() => setIsZoomed(false)} data-id="element-235" data-mp="oyopjl197s64">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-label="Close enlarged image" data-id="element-236" data-mp="17sk7rx9nj2hi">
              <X size={22} data-id="element-237" data-mp="1vfimape7o0cs" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-238" data-mp="1vhd5nz9u3ker" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}